import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { signIn, useSession } from 'next-auth/react';
import Login, { LoginProps } from 'components/commercetools-ui/organisms/authentication/login';
import Tile from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/authentication/tile';
import { Overlay, isSSOEnabled } from '@loomstateorg/shared-frontend';
import Redirect from '@loomstateorg/shared-frontend/helpers/redirect';
import { useAccount } from 'frontastic/hooks';
import { Reference } from 'types/reference';
import { TasticWithCMS } from 'types/tastic';

export interface Props extends TasticWithCMS<unknown, unknown, any> {}

const AccountLoginTastic: React.FC<Props> = ({ data }) => {
  const cmsData = data?.cmsData;
  const fields = (cmsData?.dataSource as any)?.fields || (cmsData?.dataSource as any)?.items?.[0]?.fields;

  const [imageWidth, setImageWidth] = useState(0);
  const router = useRouter();
  const { status: sessionStatus, data: session } = useSession();
  const { loggedIn } = useAccount();
  const ssoEnabled = isSSOEnabled();

  const getImageUrl = (image: any) => image?.fields?.file?.url || image?.url || image;

  const accountDataLinks: LoginProps = useMemo(
    () => ({
      accountLink: '/' as unknown as Reference,
      signInLink: '/login' as unknown as Reference,
      logo: fields ? getImageUrl(fields.logo) : undefined,
      src: fields ? getImageUrl(fields.logo) : undefined,
    }),
    [fields]
  );

  const redirectLink = useMemo(() => {
    const lastVisitedPage = router.query.lvp ? `/${router.query.lvp}` : accountDataLinks.accountLink;
    return lastVisitedPage;
  }, [accountDataLinks.accountLink, router]);

  useEffect(() => {
    if (fields?.backgroundImageWidth) {
      setImageWidth(Number(fields.backgroundImageWidth) - 100);
    }
  }, [fields?.backgroundImageWidth]);

  useEffect(() => {
    if (!ssoEnabled) return;
    if (sessionStatus === 'loading' || session) return;
    void signIn('keycloak', { callbackUrl: '/' });
  }, [ssoEnabled, sessionStatus, session]);

  if (loggedIn) return <Redirect target={redirectLink} />;

  if (ssoEnabled) {
    return <Overlay />;
  }

  if (!fields) {
    return <div>No data</div>;
  }

  return (
    <div className="relative isolate flex h-screen items-center justify-center overflow-hidden bg-center">
      <div
        className="absolute inset-0"
        style={{
          zIndex: -10,
          backgroundColor: `${fields.backgroundColor}`,
        }}
      />
      <img
        src={getImageUrl(fields.backgroundImage)}
        alt="Background"
        className="absolute inset-0 -z-10 h-full w-full object-cover"
        style={{ transform: `translateX(${imageWidth}%)` }}
      />
      <div className="relative z-10 grid grid-cols-1 items-center gap-48 md:grid-cols-3 md:gap-32">
        <div className=""></div>
        <div className="rounded-lg bg-white px-16 py-28 lg:p-[48px]">
          <Login {...accountDataLinks} />
        </div>
        <div className="hidden max-w-[320px] md:block">
          <Tile icon={getImageUrl(fields.icon)} text={fields.shortText} title={fields.title} />
        </div>
      </div>
    </div>
  );
};

export default AccountLoginTastic;
